<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte/internal'
    import { faUser, faAdd, faAngleLeft, faCalendarCheck, faPlusCircle, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import Zact from '../_common/z/zact.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zgrid from '../_common/z/zgrid.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import ZContainerCol from '../_common/z/z-container-col.svelte'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Popover from '../_common/popover.svelte'
    import ZentitySelect from '../_common/z/zentity-select.svelte'
    import ServiceSelect2 from '../pos/service-select-2.svelte'
    import { GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridItemShow, GridLine } from '../_common/z/zgrid'
    import { WaitlistEntryEntity } from '../../modules/schedulerApi/waitlist-entries/waitlist-entry.entity'
    import type { CreateWaitlistEntryDto } from '../../modules/schedulerApi/waitlist-entries/dto/create-waitlist-entry.dto'
    import type { UpdateWaitlistEntryDto } from '../../modules/schedulerApi/waitlist-entries/dto/update-waitlist-entry.dto'
    import type { Schedule } from '../../modules/schedulerApi/schedules/schedule'
    import { Person } from '../../modules/eventsApi/persons/person'
    import Zflow from '../_common/z/zflow.svelte'
    import ZinputTitle from '../_common/z/zinput-title.svelte'
    import ZinputBreak from '../_common/z/zinput-break.svelte'
    import type { Service } from '../../modules/posApi/services/service'
    import { faEdit } from '@fortawesome/free-regular-svg-icons'
    import Zexpand from '../_common/z/zexpand.svelte'
    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    const MINIMUM_DURATION_MINUTES = 10

    let show = false
    let step = 1
    let entities: WaitlistEntryEntity[] = []
    let personsById: Record<string, Person> = {}
    let entity: WaitlistEntryEntity = new WaitlistEntryEntity()
    let withDate = false
    let withTime = false
    let withWeek = false

    // dados de apoio (agendas, serviços, profissionais)
    let schedules: Schedule[] = []
    let services: Service[] = []
    let userOptions: { value: string; label: string }[] = []

    const weekdayOptions = [
        { value: 0, label: 'Domingo' },
        { value: 1, label: 'Segunda' },
        { value: 2, label: 'Terça' },
        { value: 3, label: 'Quarta' },
        { value: 4, label: 'Quinta' },
        { value: 5, label: 'Sexta' },
        { value: 6, label: 'Sábado' },
    ]
    const weekdayShort = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

    // estado do editor
    let person: Person = null
    let rowKeySeq = 0
    let entryServices: { key: number; service: Service; userId: string; parentServicesOnInvoicesId: string }[] = []
    let durationMinutes = 30
    let durationManuallyEdited = false
    let dateStart: Date = null
    let dateEnd: Date = null
    let timeStart: Date = null
    let timeEnd: Date = null
    let weekdays: number[] = []
    let notes = ''

    async function loadSupportData() {
        schedules = await window.api.schedulers.schedules.getAll()
        services = await window.api.pos.services.getAll(undefined, 600)
        const companyUsers = await window.api.users.companies.getCompanyUsers(300)
        userOptions = companyUsers.map((el) => ({ value: el.userId, label: el.userName }))
    }

    function serviceName(serviceId: string) {
        return services.find((el) => el.serviceId === serviceId)?.name || '[serviço não encontrado]'
    }

    function userName(userId: string) {
        return userOptions.find((el) => el.value === userId)?.label || ''
    }

    function scheduleName(scheduleId: string) {
        return schedules.find((el) => el.scheduleId === scheduleId)?.label || '[agenda não encontrada]'
    }

    async function loadEntities() {
        try {
            show = true
            step = 1
            entities = await window.api.schedulers.waitlistEntries.getAll()
            await loadPersons()
            loadGrid()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function loadPersons() {
        const missingIds = [...new Set(entities.map((el) => el.personId))].filter((id) => id && !personsById[id])
        const founds = await Promise.all(missingIds.map((id) => window.api.events.persons.getOne(id).catch(() => null)))
        for (let i = 0; i < missingIds.length; i++) {
            if (founds[i]) personsById[missingIds[i]] = founds[i]
        }
    }

    function formatDatePreference(item: WaitlistEntryEntity) {
        if (!item.dateStart && !item.dateEnd) return 'Qualquer data'
        const from = item.dateStart ? new Date(item.dateStart).toLocaleDateString('pt-BR') : '…'
        const to = item.dateEnd ? new Date(item.dateEnd).toLocaleDateString('pt-BR') : '…'
        return from + ' a ' + to
    }

    function formatTimePreference(item: WaitlistEntryEntity) {
        if (item.timeStartMinute == null && item.timeEndMinute == null) return 'Qualquer horário'
        const from = item.timeStartMinute != null ? minutesToLabel(item.timeStartMinute) : '00:00'
        const to = item.timeEndMinute != null ? minutesToLabel(item.timeEndMinute) : '23:59'
        return from + ' às ' + to
    }

    function minutesToLabel(minutes: number) {
        const h = Math.floor(minutes / 60)
        const m = minutes % 60
        return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0')
    }

    function formatWeekdays(item: WaitlistEntryEntity) {
        if (!item.weekdays || item.weekdays.length === 0 || item.weekdays.length === 7) return 'Todos os dias'
        return [...item.weekdays]
            .sort()
            .map((el) => weekdayShort[el])
            .join(', ')
    }

    function formatServices(item: WaitlistEntryEntity) {
        return (item.services || [])
            .map((el) => {
                const name = serviceName(el.serviceId)
                return el.userId ? name + ' (' + userName(el.userId) + ')' : name
            })
            .join(', ')
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        grid = new GridClass()
        grid.fontSize = '13px'
        grid.title = 'Lista de espera'
        grid.cols.push(new GridCol({ content: 'Cliente', type: GridColTypeEnum.string }))

        grid.cols.push(new GridCol({ content: 'Serviços desejados', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.string, mobile: GridItemShow.hide }))
        grid.cols.push(new GridCol({ content: 'Horário', type: GridColTypeEnum.string, mobile: GridItemShow.hide }))
        grid.cols.push(new GridCol({ content: 'Vagas', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))

        for (const item of entities) {
            const matchCount = item.matches?.length || 0
            const badge =
                matchCount > 0
                    ? new GridBadge({
                          label: matchCount + (matchCount === 1 ? ' disponível' : ' disponíveis'),
                          class: GridBadgeClassEnum.badge_success,
                      })
                    : new GridBadge({ label: 'Nenhum disponível', class: GridBadgeClassEnum.badge_disabled })

            const buttons: GridButton[] = []
            if (matchCount > 0) {
                buttons.push(new GridButton({ icon: faCalendarCheck, color: 'primary', label: 'Ver', callback: () => showMatches(item) }))
            }
            buttons.push(new GridButton({ icon: faUser, callback: () => window.person('show', item.personId) }))
            buttons.push(new GridButton({ icon: faTrashAlt, callback: () => removeEntity(item.waitlistEntryId) }))
            buttons.push(new GridButton({ icon: faEdit, callback: () => editEntity(item.waitlistEntryId) }))

            const line = new GridLine()
            line.slots.push({ content: personsById[item.personId]?.name || '[cliente não encontrado]' })
            line.slots.push({ content: formatServices(item) })
            line.slots.push({ content: formatDatePreference(item) })
            line.slots.push({ content: formatTimePreference(item) })
            line.slots.push({ content: badge })
            line.slots.push({ content: buttons })
            grid.lines.push(line)
        }
    }

    // ---- ver vagas ----
    let matchesEntry: WaitlistEntryEntity = null
    let matchesGrid: GridClass = new GridClass()
    function showMatches(item: WaitlistEntryEntity) {
        matchesEntry = item
        loadMatchesGrid()
    }
    function hideMatches() {
        matchesEntry = null
    }

    function loadMatchesGrid() {
        matchesGrid = new GridClass()
        matchesGrid.cols.push(new GridCol({ content: 'Dia', type: GridColTypeEnum.string }))
        matchesGrid.cols.push(new GridCol({ content: 'Horário', type: GridColTypeEnum.string }))
        matchesGrid.cols.push(new GridCol({ content: 'Agenda', type: GridColTypeEnum.string }))
        matchesGrid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))

        for (const match of matchesEntry.matches || []) {
            const line = new GridLine()
            line.slots.push({ content: new Date(match.start).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit' }) })
            line.slots.push({
                content:
                    new Date(match.start).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) +
                    ' - ' +
                    new Date(match.end).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
            })
            line.slots.push({ content: scheduleName(match.scheduleId) })
            line.slots.push({ content: [new GridButton({ label: 'Agendar', color: 'primary', callback: () => selectMatch(match) })] })
            matchesGrid.lines.push(line)
        }
    }

    function selectMatch(match: { scheduleId: string; start: Date; end: Date }) {
        dispatch('selectSlot', {
            scheduleId: match.scheduleId,
            start: new Date(match.start),
            end: new Date(match.end),
            personId: matchesEntry.personId,
            items: (matchesEntry.services || []).map((el) => ({
                serviceId: el.serviceId,
                userId: el.userId,
                parentServicesOnInvoicesId: el.parentServicesOnInvoicesId,
            })),
        })
        matchesEntry = null
        dispatch('close')
    }

    // ---- editor ----
    function newEntity() {
        entity = new WaitlistEntryEntity()
        person = null
        rowKeySeq = 0
        entryServices = []
        durationMinutes = 30
        durationManuallyEdited = false
        weekdays = [0, 1, 2, 3, 4, 5, 6]
        dateStart = null
        dateEnd = null
        timeStart = null
        timeEnd = null
        notes = ''
        withDate = false
        withTime = false
        withWeek = false
        step = 2
    }

    function onPersonChange() {
        if (!person) entryServices = []
    }

    async function editEntity(id: string) {
        try {
            show = true
            entity = await window.api.schedulers.waitlistEntries.getOne(id)
            person = personsById[entity.personId] || (await window.api.events.persons.getOne(entity.personId).catch(() => null))
            rowKeySeq = 0
            entryServices = (entity.services || []).map((el) => ({
                key: rowKeySeq++,
                service: services.find((s) => s.serviceId === el.serviceId) || null,
                userId: el.userId || null,
                parentServicesOnInvoicesId: el.parentServicesOnInvoicesId || null,
            }))
            if (entryServices.length === 0) entryServices = [{ key: rowKeySeq++, service: null, userId: null, parentServicesOnInvoicesId: null }]
            durationMinutes = entity.durationMinutes
            durationManuallyEdited = false
            dateStart = entity.dateStart ? new Date(entity.dateStart) : null
            dateEnd = entity.dateEnd ? new Date(entity.dateEnd) : null
            const base = new Date()
            base.setHours(0, 0, 0, 0)
            timeStart = entity.timeStartMinute != null ? addMinutes(base, entity.timeStartMinute) : null
            timeEnd = entity.timeEndMinute != null ? addMinutes(base, entity.timeEndMinute) : null
            weekdays = entity.weekdays?.length > 0 ? [...entity.weekdays] : [0, 1, 2, 3, 4, 5, 6]
            notes = entity.notes || ''
            step = 2
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function addMinutes(base: Date, minutes: number) {
        const result = new Date(base)
        result.setMinutes(result.getMinutes() + minutes)
        return result
    }

    function addServiceRow() {
        entryServices = [...entryServices, { key: rowKeySeq++, service: null, userId: null, parentServicesOnInvoicesId: null }]
    }

    function removeServiceRow(key: number) {
        entryServices = entryServices.filter((el) => el.key !== key)
    }

    function onDurationChangedManually() {
        durationManuallyEdited = true
    }

    // recalcula o intervalo automaticamente pela soma dos serviços, até o usuário editar manualmente o campo
    $: if (!durationManuallyEdited) {
        const sum = entryServices.reduce((acc, row) => acc + (row.service?.duration || 0), 0)
        if (sum > 0) {
            durationMinutes = sum
        }
    }

    function showEntities() {
        step = 1
    }

    function evaluateError(error) {
        window.error(error)
    }

    function buildServicesDto() {
        return entryServices
            .filter((el) => !!el.service?.serviceId)
            .map((el) => ({
                serviceId: el.service.serviceId,
                userId: el.userId || undefined,
                parentServicesOnInvoicesId: el.parentServicesOnInvoicesId || undefined,
            }))
    }

    function validate() {
        if (!person?.personId) {
            window.error('Selecione o cliente.')
            return false
        }
        if (buildServicesDto().length === 0) {
            window.error('Adicione ao menos um serviço.')
            return false
        }
        if (weekdays.length === 0) {
            window.error('Selecione ao menos um dia da semana.')
            return false
        }
        if (dateStart && dateEnd && +new Date(dateEnd) < +new Date(dateStart)) {
            window.error('A data final deve ser posterior à data de início.')
            return false
        }
        if (!durationMinutes || durationMinutes < MINIMUM_DURATION_MINUTES) {
            window.error('O intervalo necessário deve ser de ao menos ' + MINIMUM_DURATION_MINUTES + ' minutos.')
            return false
        }
        return true
    }

    async function saveEntity() {
        if (!validate()) return
        if (entity.waitlistEntryId) {
            await updateEntity()
        } else {
            await createEntity()
        }
    }

    async function createEntity() {
        try {
            show = true
            const dto: CreateWaitlistEntryDto = {
                personId: person.personId,
                services: buildServicesDto(),
                durationMinutes,
                dateStart: dateStart || undefined,
                dateEnd: dateEnd || undefined,
                timeStartMinute: timeStart ? minutesFromDate(timeStart) : undefined,
                timeEndMinute: timeEnd ? minutesFromDate(timeEnd) : undefined,
                weekdays,
                notes,
            }
            await window.api.schedulers.waitlistEntries.create(dto)
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateEntity() {
        try {
            show = true
            const dto: UpdateWaitlistEntryDto = {
                personId: person.personId,
                services: buildServicesDto(),
                durationMinutes,
                dateStart: dateStart || null,
                dateEnd: dateEnd || null,
                timeStartMinute: timeStart ? minutesFromDate(timeStart) : null,
                timeEndMinute: timeEnd ? minutesFromDate(timeEnd) : null,
                weekdays,
                notes,
            }
            await window.api.schedulers.waitlistEntries.update(entity.waitlistEntryId, dto)
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function minutesFromDate(date: Date) {
        // campo de horário limpo devolve vazio (ver zinput-date-value)
        if (!date || isNaN(new Date(date).getTime())) return null
        return date.getHours() * 60 + date.getMinutes()
    }

    async function removeEntity(id: string) {
        if (!confirm('Deseja mesmo excluir este registro da lista de espera?')) return
        try {
            show = true
            await window.api.schedulers.waitlistEntries.remove(id)
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    onMount(async () => {
        await loadSupportData()
        await loadEntities()
    })
</script>

{#if matchesEntry}
    <Popover on:close={hideMatches} title="Vagas disponíveis" width="700px">
        <Zgrid class="m-3" grid={matchesGrid} />
    </Popover>
{/if}

{#if step === 1}
    <Content>
        <Zact>
            <div class="spacer" />
            <Zbutton color="primary" icon={faPlusCircle} label="Novo" mobileHideLabel={true} on:click={newEntity} />
        </Zact>
        <Zgrid class="m-3" {grid} />
        <LoadSpinner {show} />
    </Content>
{/if}

{#if step === 2}
    <Content>
        <Zact>
            <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={showEntities} />
            <div class="spacer" />
            {#if !!entity.waitlistEntryId}
                <Zbutton color="red" icon={faTrashAlt} label="Excluir" disabled={show} on:click={() => removeEntity(entity.waitlistEntryId)} />
            {/if}
            <Zbutton color="primary" icon={faSave} label="Salvar" loading={show} disabled={show} on:click={saveEntity} />
        </Zact>

        <Zflow class="m-3">
            <Zframe title="Cadastro">
                <ZContainer gapRow="8px" gapCol="8px">
                    <ZContainerCol size={12}>
                        <ZentitySelect fonte="pessoa" label="Cliente" bind:value={person} on:change={onPersonChange} />
                    </ZContainerCol>

                    <Zframe class="mt-2">
                        {#each entryServices as row (row.key)}
                            <ZContainer gapRow="8px" gapCol="8px" class="flex-grow mt-2">
                                <ZContainerCol size={6} sizeM={12}>
                                    <ServiceSelect2
                                        label="Serviço"
                                        balance={true}
                                        v2={true}
                                        type="service"
                                        duration={true}
                                        price={false}
                                        bind:service={row.service}
                                        bind:balance_parentServicesOnInvoicesId={row.parentServicesOnInvoicesId}
                                        personId={person?.personId}
                                    />
                                </ZContainerCol>
                                <Zinput label="Profissional (opcional)" size={5} sizeM={11} type="select" options={userOptions} bind:value={row.userId} />
                                <ZContainerCol size={1} sizeM={1} verticalAlign="center">
                                    <Zbutton color="red" icon={faTrashAlt} on:click={() => removeServiceRow(row.key)} disabled={entryServices.length === 1} />
                                </ZContainerCol>
                            </ZContainer>
                        {/each}
                        <Zbutton class="mt-2" color="secondary" icon={faAdd} label="Adicionar serviço" on:click={addServiceRow} />
                    </Zframe>
                </ZContainer>
                <Zexpand label="Exibir configurações" class="mt-3 mb-2">
                    <Zframe>
                        <ZContainer>
                            <Zinput
                                label="Intervalo necessário (minutos)"
                                size={4}
                                sizeM={12}
                                type="number"
                                bind:value={durationMinutes}
                                on:change={onDurationChangedManually}
                                help="Calculado automaticamente pela soma dos serviços selecionados. Ao editar manualmente, deixa de ser recalculado."
                            />
                            <Zinput label="Observações (opcional)" size={8} sizeM={12} type="text" bind:value={notes} />
                            <Zinput size={4} type="toggle" bind:value={withWeek} label="Com preferência de dia da semana"></Zinput>
                            <Zinput
                                disabled={!withWeek}
                                label="Dias da semana"
                                size={8}
                                sizeM={12}
                                type="multiselect"
                                options={weekdayOptions}
                                bind:value={weekdays}
                                help="Desmarque os dias que não servem para este cliente."
                            />

                            <Zinput size={4} type="toggle" bind:value={withDate} label="Com preferência de data"></Zinput>
                            <Zinput
                                disabled={!withDate}
                                label="Data de início (opcional)"
                                size={4}
                                sizeM={6}
                                type="date"
                                bind:value={dateStart}
                                help="Deixe em branco para qualquer data."
                            />
                            <Zinput
                                disabled={!withDate}
                                label="Data final (opcional)"
                                size={4}
                                sizeM={6}
                                type="date"
                                bind:value={dateEnd}
                                help="Deixe em branco para qualquer data."
                            />
                            <Zinput size={4} type="toggle" bind:value={withTime} label="Com preferência de horário"></Zinput>
                            <Zinput
                                disabled={!withTime}
                                label="Horário de início (opcional)"
                                size={4}
                                sizeM={6}
                                type="time"
                                bind:value={timeStart}
                                help="Deixe em branco para qualquer horário."
                            />

                            <Zinput
                                disabled={!withTime}
                                label="Horário final (opcional)"
                                size={4}
                                sizeM={6}
                                type="time"
                                bind:value={timeEnd}
                                help="Deixe em branco para qualquer horário."
                            />
                        </ZContainer>
                    </Zframe>
                </Zexpand>
            </Zframe>
        </Zflow>
        <LoadSpinner {show} />
    </Content>
{/if}
